import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import {
    useGetSectorAssignmentsQuery,
    useCreateAnalystAssignmentMutation,
    useUpdateAnalystAssignmentMutation,
} from "../../../redux/service/modules/admin";
import { useNotification } from "../../../context/NotificationContext";
import { ActionButtons } from "../../../components/common/ActionButton";
import { useGetAnalystListQuery } from "../../../redux/service/modules/dashboard";
import CommonDialog from "../../../components/common/CommonDialog";

const AnalystAssignmentDialog = ({
    open,
    onClose,
    rowData,
    isEdit,
    title,
    ctaText,
}) => {
    const { control, handleSubmit, setValue, reset } = useForm();
    const [updateAnalystAssignment, { isLoading }] =
        useUpdateAnalystAssignmentMutation();
    const [createAnalystAssignment, { isCreating }] =
        useCreateAnalystAssignmentMutation();
    const { data: sectors } = useGetSectorAssignmentsQuery({});
    const [sectorsData, setSectorsData] = useState([]);
    const { data: analysts } = useGetAnalystListQuery({});
    const { showNotification } = useNotification();

    useEffect(() => {
        let filterData;
        if (isEdit) {
            filterData = sectors;
        } else {
            filterData = sectors?.filter((sector) => sector.analyst === null);
        }
        setSectorsData(filterData);
    }, [sectors, isEdit]);

    useEffect(() => {
        if (rowData) {
            Object.keys(rowData).forEach((key) => {
                if (key === "analyst_id") {
                    setValue("analyst", rowData[key]);
                } else if (key === "sector_id") {
                    setValue("sector", rowData[key]);
                } else {
                    setValue(key, rowData[key]);
                }
            });
        }
    }, [rowData, setValue]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data) => {
        try {
            const formDataToSend = {};
            Object.keys(data).forEach((key) => {
                if (data[key] !== undefined) {
                    formDataToSend[key] = data[key];
                }
            });
            if (isEdit) {
                await updateAnalystAssignment({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Analyst Assignment Updated successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Update Analyst Assignment"
                            );
                        }
                    });
            } else {
                await createAnalystAssignment({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Analyst Assignment Created successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Create Analyst Assignment"
                            );
                            handleClose();
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            showNotification("error", error.data.error);
                            handleClose();
                        }
                    });
            }
        } catch (error) {
            console.error("Failed to update analyst assignment:", error);
        }
    };

    return (
        <CommonDialog open={open} onClose={handleClose} title={title}>
            <div className="space-y-2">
                <label className="block xl:text-xs 2xl:text-sm 3xl:text-base font-medium text-gray-700">
                    Sector
                </label>
                <Controller
                    name="sector"
                    control={control}
                    rules={{ required: "Sector is required" }}
                    disabled={isEdit}
                    render={({ field, fieldState }) => (
                        <div className="relative">
                            <select
                                {...field}
                                disabled={isEdit}
                                className="w-full xl:px-2 2xl:px-3 3xl:px-4 xl:py-1.5 2xl:py-2 3xl:py-2.5 xl:text-xs 2xl:text-sm 3xl:text-base
                                xl:border xl:rounded-md 2xl:border-2 2xl:rounded-lg 3xl:border-2 3xl:rounded-lg
                                border-gray-200 focus:outline-none xl:focus:ring-1 2xl:focus:ring-2 3xl:focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors
                                disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                                value={field.value || ""}
                                onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                }
                            >
                                <option value="" disabled selected>
                                    Select Sector
                                </option>
                                {sectorsData?.map((sector) => (
                                    <option
                                        key={sector.sector.id}
                                        value={sector.sector.id}
                                    >
                                        {sector.sector.name}
                                    </option>
                                ))}
                            </select>
                            {fieldState.error && (
                                <span className="block xl:text-xs 2xl:text-sm 3xl:text-base text-red-500 mt-1">
                                    {fieldState.error.message}
                                </span>
                            )}
                        </div>
                    )}
                />
            </div>

            <div className="space-y-2">
                <label className="block xl:text-xs 2xl:text-sm 3xl:text-base font-medium text-gray-700">
                    Analyst
                </label>
                <Controller
                    name="analyst"
                    control={control}
                    rules={{ required: "Analyst is required" }}
                    render={({ field, fieldState }) => (
                        <div className="relative">
                            <select
                                {...field}
                                className="w-full xl:px-2 2xl:px-3 3xl:px-4 xl:py-1.5 2xl:py-2 3xl:py-2.5 xl:text-xs 2xl:text-sm 3xl:text-base
                                xl:border xl:rounded-md 2xl:border-2 2xl:rounded-lg 3xl:border-2 3xl:rounded-lg
                                border-gray-200 focus:outline-none xl:focus:ring-1 2xl:focus:ring-2 3xl:focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                                value={field.value || ""}
                                onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                }
                            >
                                <option value="" disabled selected>
                                    Select Analyst
                                </option>
                                {analysts?.map((analyst) => (
                                    <option key={analyst.id} value={analyst.id}>
                                        {analyst.username} - {analyst.email}
                                    </option>
                                ))}
                            </select>
                            {fieldState.error && (
                                <span className="block xl:text-xs 2xl:text-sm 3xl:text-base text-red-500 mt-1">
                                    {fieldState.error.message}
                                </span>
                            )}
                        </div>
                    )}
                />
            </div>

            <ActionButtons
                onCancel={handleClose}
                ctaText={ctaText}
                onSave={handleSubmit(onSubmit)}
                isLoading={isLoading || isCreating}
            />
        </CommonDialog>
    );
};

AnalystAssignmentDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
    isEdit: PropTypes.bool,
    title: PropTypes.string,
    ctaText: PropTypes.string,
};

export default AnalystAssignmentDialog;
